import { ReactElement } from "react";
import Head from "next/head";
import Image from "next/image";

import { MainLayout } from "@/containers/layout/LandingLayout";

const Privacy = () => {
  return (
    <>
      <Head>
        <title>Keyzy | Privacy Notice</title>
        <link rel="canonical" href="https://www.keyzy.com/privacy" />
        <meta
          name="description" key="description"
          content="Your privacy is important to us. This page gives you an idea of how we collect your information, why we collect it, and how we process it."
        />
        <meta
          name="twitter:card"
          content="Your privacy is important to us. This page gives you an idea of how we collect your information, why we collect it, and how we process it."
          key="twcard"
        />
        <meta name="twitter:creator" content="keyzyhomes" key="twhandle" />
        <meta property="og:url" content="/" key="ogurl" />
        <meta
          property="og:image"
          content="https://www.keyzy.com/landing/keyzy-meta-image.png"
          key="ogimage"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta property="og:title" content="Keyzy | Privacy Notice" key="ogtitle" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Your privacy is important to us. This page gives you an idea of how we collect your information, why we collect it, and how we process it."
          key="ogdesc"
        />
      </Head>

      <div className="relative px-5 mt-10 mb-10 xl:mb-16 xl:mt-20">
        <div className="relative text-left xl:h-32 xl:text-center mb-14">
          <div className="absolute hidden transform rotate-180 -left-10 -top-5 w-96 h-36 xl:inline-block">
            <Image
              src="/shared/shapes.svg"
              alt="shapes doodles"
              objectFit="contain"
              layout="fill"
              priority
            />
          </div>

          <h1 className="mb-10 font-bold max-w-[52.75rem] mx-auto xl:max-w-none text-4xl xs:text-[44px] md:text-[56px] xl:text-[76px] xs:leading-[46px] md:leading-[59px] xl:leading-[80px]">
            Privacy Notice
          </h1>
          <div className="font-serif text-3xl md:mt-6 break-words max-w-[52.75rem] mx-auto font-normal">
            Learn how we use and protect your personal data
          </div>

          <div className="absolute hidden -right-10 -top-5 w-96 h-36 xl:inline-block">
            <Image
              src="/shared/shapes.svg"
              alt="shapes doodles 2"
              objectFit="contain"
              layout="fill"
              priority
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col p-5 max-w-[52.75rem] w-full mx-auto">
        <p>
          By registering with Keyzy (a trading name of Kollitom Ltd) you are entrusting us with your
          personal data. Your privacy is protected by law, and it is also protected by our privacy
          policies. This page gives you an idea of how we use your data and the safeguards we put in
          place to protect it.
        </p>
        <br className="br-2x" />
        <p>
          You can contact our voluntarily appointed Data Protection Officer at{" "}
          <a href="mailto:privacy@keyzy.com" target="_blank" rel="noreferrer" className="underline">
            privacy@keyzy.com
          </a>
          . If you have any concerns or wish to exercise your rights or you can write to us at Keyzy
          (Kollitom Ltd), LABS Atrium, Chalk Farm Road, London, NW1 8AH.
        </p>
        <h2 className="mb-6 text-xl font-bold mt-7">Our Promises:</h2>
        <div className="">
          To help you on your homeowner journey we will need data about you and your circumstances.
          To make this as relevant to you we will need data about you and your circumstances and in
          some cases share this with partners. We make the following promises about how we will
          treat this data:{" "}
          <ul className="pl-6 list-disc">
            <li className="mt-2">
              We will only collect data about you that is relevant and necessary;
            </li>
            <li className="mt-2">
              Your data will only be held on systems that meet compliance standards;{" "}
            </li>
            <li className="mt-2">
              Your data will only be accessed by those who need it, and we will minimise the amount
              of data that is processed, wherever possible;{" "}
            </li>
            <li className="mt-2">
              We will only share data with third parties that meet our own privacy standards for the
              purposes of trying to secure you funding unless either you have agreed to share data,
              we are required to share it by law or we need to fulfil our service commitments to you
              through credit reference agencies (see below) and other partners;
            </li>
            <li className="mt-2">
              We will always remember that it is your personal data, not ours. As such we will
              ensure complete transparency and openness with you wherever possible.
            </li>
            <li className="mt-2">
              We respect your rights as outlined in the next section and will respond to all
              requests promptly.
            </li>
          </ul>
          <br className="br-3x" />
          Or, you can email us on{" "}
          <a href="mailto:privacy@keyzy.com" target="_blank" rel="noreferrer" className="underline">
            privacy@keyzy.com
          </a>
        </div>
        <h2 className="mb-6 text-xl font-bold mt-7">Your Rights:</h2>
        <div className="">
          You may have the following rights over any data we hold about you:
          <ul className="pl-6 list-disc">
            <li className="mt-2">
              <b>Your right of access</b> – You have the right to ask us for copies of your personal
              information.
            </li>
            <li className="mt-2">
              <b>Your right to rectification</b> – You have the right to ask us to rectify personal
              information you think is inaccurate. You also have the right to ask us to complete
              information you think is incomplete.
            </li>
            <li className="mt-2">
              <b>Your right to erasure</b> – You have the right to ask us to erase your personal
              information in certain circumstances.
            </li>
            <li className="mt-2">
              <b>Your right to restriction of processing</b> – You have the right to ask us to
              restrict the processing of your personal information in certain circumstances.
            </li>
            <li className="mt-2">
              <b>Your right to object to processing</b> – You have the right to object to the
              processing of your personal information in certain circumstances.
            </li>
            <li className="mt-2">
              <b>Your right to data portability</b> – You have the right to ask that we transfer the
              personal information you gave us to another organisation, or to you, in certain
              circumstances.
            </li>
          </ul>
          <br className="br-2x" />
          You can read more about your rights{" "}
          <a
            href="http://knowyourprivacyrights.org/"
            target="_blank"
            className="underline"
            rel="noreferrer"
          >
            here
          </a>
          .
          <br className="br-2x" />
          If you would like to uphold your rights then please contact our Data Protection Officer at{" "}
          <a href="mailto:privacy@keyzy.com" target="_blank" rel="noreferrer" className="underline">
            privacy@keyzy.com
          </a>
          .
          <br className="br-2x" />
          If you are dissatisfied with our response you also have the right to lodge a complaint
          with the Data Protection Authority. This can be done at{" "}
          <a
            href="https://ico.org.uk/concerns/"
            target="_blank"
            className="underline"
            rel="noreferrer"
          >
            https://ico.org.uk/concerns/
          </a>
          .
        </div>
        <h2 className="mb-6 text-xl font-bold mt-7">Collecting Data:</h2>
        <div className="">
          We collect information about you in two key ways:
          <ul className="pl-6 list-disc">
            <li className="mt-2">
              <b>From you</b> – The more information you provide the better the insights we can
              provide and the easier it is to align your circumstances with our partners. For
              example, you may share information for us to assess your suitability for a mortgage;
              banking transactions; car and other insurances or other data associated with a product
              you express an interest in or you signed up or our newsletter, or met one of us at
              events or meetings or have approached us on social media ;
            </li>
            <li className="mt-2">
              <b>From others</b> – this is data about you that we may hold from credit rating
              agencies, Anti Money Laundering (AML) and Know Your Customer (KYC) check providers,
              mortgage providers, especially if you have had a mortgage offer rejected, mortgage
              brokers and their agents or from proactive marketing activity and referrals. We may
              also receive data from other partners we use for prizes, competitions and offers.
              Finally, we may identify you for marketing through LinkedIn or other advertisements.
            </li>
          </ul>
          <br className="br-2x" />
          We try and minimise the personal data held on you. Typically, this is restricted to: the
          information needed to buy a home with our partners and investors:
          <ul className="pl-6 list-disc">
            <li className="mt-2">
              <b>Identity Data</b> including your first name, last name, current and previous
              address and date of birth. We may also ask you for a selfie or a picture of your
              passport or driving license to confirm identification as part of our legal
              responsibilities.
            </li>
            <li className="mt-2">
              <b>Contact Data</b> including email address, telephone numbers, home address, employer
              address, employer name.
            </li>
            <li className="mt-2">
              <b>Account Data</b> including information you give us when you register your account.
            </li>
            <li className="mt-2">
              <b>Financial Data</b> including account and card payment details, bank income, NI
              Number, savings, spending, linked bank account details and credit history and
              verification of this data (see below).
            </li>
            <li className="mt-2">
              <b>Technical Data</b> including internet protocol (IP) address, browser type and
              version, location, browser plug-in types and versions, operating system and platform,
              and other technology on the devices you use to access the website and services.
            </li>
            <li className="mt-2">
              <b>Application Data</b> including details about your family and living situation,
              property preferences and housing history.
            </li>
            <li className="mt-2">
              <b>Usage Data</b> including information about how you use our website and services.
            </li>
            <li className="mt-2">
              <b>Marketing and Communications Data</b> including your consent, if required, to get
              updates from us.
            </li>
          </ul>
          <br className="br-2x" />
          Our website and other materials sent to you may contain links to other third party
          websites. We may also offer buttons to social media that link to third party services.
          We’re not responsible for the content or the data privacy these sites provide through
          their tools or sites.
          <h3 className="font-bold mt-6 mb-3">Special Categories of Data</h3>
          In some cases, we may need to process &quot;special categories&quot; of your personal
          data. This data requires a higher level of governance. We will not usually ask for this
          type of data, however, if you belong to a union for example and link your accounts and
          spending through the app or you provide us with other special category data, for example
          to let us know that you’re vulnerable we will be processing this data. This data will not
          be used for any other purpose whilst identified to you and by using the app you
          acknowledge and agree that this processing is done with your consent.
          <br className="br-2x" />
          We will only ask about special categories of data if we think it is causing a difficulty
          with your account. We will always explain why we are asking for it and how we might use
          it.
        </div>
        <h2 className="mb-6 text-xl font-bold mt-7">Processing your Data</h2>
        <p>
          Data is processed/stored locally and on encrypted third party hosted cloud services. We
          use several different platforms to deliver our services including Microsoft, Box, Monday,
          Google, Meta and Hotjar. In addition, we may use Large Language Models (LLM) to help us
          fulfil some of our services. A list of these systems can be provided on request. These
          services all have strong data security at the heart of their systems including ISO27001
          and SOC2 certification. We ensure that access to these services is strictly controlled and
          include strong authentication processes like Multi Factor Authentication.
          <br className="br-2x" />
          Due to the service providers locations, some data will either be in UK, EEA/EU data
          centres or on US based servers that have demonstrated strong Data Security. We may also
          process your data in countries outside the UK or European Union from time to time in other
          aspects of our business.
          <br className="br-2x" />
          Further to Section 119A of the Data Protection Act 2018 and noting Case C-311/18 in the
          European Court of Justice, if your data is transferred or processed outside of the UK or
          EEA we ensure the safeguards of International Data Transfer Agreements (IDTAs) or
          Addendums are enforced. Where this is not possible, we ensure that European Standard
          Contractual Clauses are entered.
          <br className="br-2x" />
          We regularly review suppliers for data security compliance to ensure your data is safe and
          track where your data is held.
          <br className="br-2x" />
          We will never ask you for your username or password for your account or ask you for any
          credentials for other applications or websites.
        </p>
        <h2 className="mb-6 text-xl font-bold mt-7">What we use your Data for</h2>
        <p>
          We use your data primarily to qualify you as a potential Keyzy customer. We might use your
          information to:
        </p>
        <ul className="pl-6 list-disc">
          <li className="mt-2">
            Assess your creditworthiness and whether you can afford to take the product;
          </li>
          <li className="mt-2">
            Ensure any offers provided to you are appropriate to your circumstances;
          </li>
          <li className="mt-2">Verify the accuracy of the data you have provided to us; </li>
          <li className="mt-2">
            Prevent criminal activity, e.g. Fraud and Anti Money Laundering (AML) and Know Your
            Customer (KYC) checks;{" "}
          </li>
          <li className="mt-2">Manage your account(s); </li>
          <li className="mt-2">Trace and recover any debts;</li>
          <li className="mt-2">
            Provide marketing information about us and our services (you can unsubscribe any time);
          </li>
          <li className="mt-2">Let you know about any changes to the website and service;</li>
          <li className="mt-2">
            Work out if you are a suitable candidate if you apply for a employment role;
          </li>
          <li className="mt-2">Collect and share credit data about you and other applicants;</li>
          <li className="mt-2">
            Link to your various financial accounts with an Open Banking provider;{" "}
          </li>
          <li className="mt-2">
            Collect for analytics purposes information about your journey with us including
            aggregating this data.
          </li>
        </ul>
        <p>
          Calls may be recorded for information holding, quality and training purposes. All our
          processes are subject to various internal policies to ensure that your data privacy and
          security is upheld.
        </p>
        <h3 className="font-bold mt-6 mb-3">Legal Basis for Processing your Data</h3>
        <p>
          Your data is only processed based on a defined legal basis. These are:
          <ul className="pl-6 list-disc mt-2">
            <li className="mt-2">
              <b>Consent</b> – For most of the data you provide us directly we ask for your consent
              to process it for the purposes of fulfilling our service to you. This consent is asked
              for when you sign up and includes your identity and financial information. We also use
              it for our marketing communication with you.{" "}
            </li>
            <li className="mt-2">
              <b>Legitimate Interest</b> – We may require using some of your data for our Legitimate
              Interests, for example data analytics and making recommendations to you.{" "}
            </li>
            <li className="mt-2">
              <b>Legal Obligation</b> – We may need to process some of your data for legal
              obligations.
            </li>
          </ul>
        </p>
        <h2 className="mb-6 text-xl font-bold mt-7">Credit Reference Checks</h2>
        <p>
          As part of our process, we carry out credit and identity checks with one or more credit
          reference agencies (CRAs). We submit your personal information to the CRAs as part of
          processing your application.{" "}
        </p>
        <p>
          This includes income and expenditure verification that will link your financial records to
          our third-party providers systems. We use TrueLayer Open Banking APIs to connect your
          financial accounts when you add them to your account. TrueLayer is a secure financial
          technology provider that connects your bank account to our service for seamless and safe
          transactions. When your financial data is linked a hard search against your credit file
          will be produced. This enables us to quickly establish that Keyzy is affordable for you.
        </p>
        <p>
          To find out more information, you can view the{" "}
          <a
            href="https://truelayer.com/legal/enduser_tos/"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            TrueLayer Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://truelayer.com/legal/privacy/"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            TrueLayer Privacy Policy
          </a>
          .
        </p>
        <p>
          If you become a Keyzy customer we may also do regular checks in the future to manage your
          account.{" "}
        </p>
        <p>
          The information regarding your rental payments and track record as a tenant, will be
          shared with Equifax Ltd. Equifax will add this information to the credit reference data it
          holds about You and use it as a controller, in accordance with its fair processing notice
          (a copy of which can be found by following the link below), including to assist other
          landlords and organisations to:
        </p>
        <ul className="pl-6 list-disc">
          <li className="pt-2">assess and manage any new tenancy agreements You may enter into;</li>
          <li className="pt-2">
            assess your financial standing to provide You with suitable products and services;
          </li>
          <li className="pt-2">
            manage any accounts that you may already hold, for example reviewing suitable products
            or adjusting your current product in light of your current circumstances;
          </li>
          <li className="pt-2">
            contact You in relation to any accounts you may have and recovering debts that you may
            owe;
          </li>
          <li className="pt-2">
            verify your identity and address to help them make decisions about services they offer;
          </li>
          <li className="pt-2">help prevent crime, fraud and money laundering;</li>
          <li className="pt-2">
            for Equifax to undertake statistical analysis, analytics and profiling,
          </li>
          <li className="pt-2">
            and for Equifax to conduct system and product testing and database processing
            activities, such as data loading, data matching and data linkage.
          </li>
        </ul>
        <p>
          We will continue to exchange information about you with CRA’s while you have a
          relationship with us. We will also inform the CRA’s about your settled accounts. If you
          borrow and do not repay in full or on time, CRA’s will record the outstanding debt. This
          information may be supplied to other organisations by CRA’s.
          <br className="br-2x" />
          When CRA’s receive a search from us they will place a search footprint on your credit file
          that may be seen by other lenders.
          <br className="br-2x" />
          If you would like to see more information on these, and to understand how the credit
          reference agencies each use and share rental data as bureau data (including the legitimate
          interests each pursues) this information is provided in this link:{" "}
          <a href="https://www.equifax.co.uk/crain" target="blank">
            www.equifax.co.uk/crain
          </a>{" "}
          (Credit Reference Agency Information Notice (CRAIN)). (For a paper copy, please get in
          touch with us or with Equifax).
          <br className="br-2x" />
          Please be aware that Equifax may continue to hold and use your information following any
          termination of your services.
        </p>
        <h2 className="mb-6 text-xl font-bold mt-7">Joint Applications</h2>
        <p>
          If you are making a joint application or tell us that you have a spouse of a financial
          associate, we will link your records together, so you should make sure you discuss this
          with them, and share with them this information, before lodging the application.
          <br className="br-2x" />
          CRA’s will also link your records together and these links will remain on your and their
          files until such time as your partner successfully files for a disassociation with the
          CRA’s to break the link.
        </p>
        <h2 className="mb-6 text-xl font-bold mt-7">Sharing your Data</h2>
        <p>
          So that we can give you the best service, we might need to share your personal information
          with other trusted third parties in addition to Credit Reference Companies. This includes
          where processing is within the same group or with related investors. For example, with our
          sister company Klink App Ltd. All partners we share data with are obligated to uphold the
          same standards of Data Protection and Security we have. When we do this, we will use the
          consent you give us as part of your application, even if you were on our wait list, you
          weren’t successful with your application or you were not the main applicant, to invite you
          to use the app.
          <br className="br-2x" />
          We may need to share your data as part of our legal obligations (for example AML checks)
          but we only share your information with other external organisations as part of delivering
          our service to you.
        </p>
        <h2 className="mb-6 text-xl font-bold mt-7">Retaining your Data</h2>
        <p>
          Dependant on the data you provide us and for what purpose it is provided we may need to
          retain your data for up to 6 years after you are no longer a customer of us. If you wish
          to find out more about your specific data retention, please contact us.
        </p>
        <h2 className="mb-6 text-xl font-bold mt-7">Data Permissions</h2>
        <p>
          Every marketing email sent from us allows you to opt out of receiving further emails from
          us, except for the purposes of fulfilling any contractual arrangements.
          <br className="br-2x" />
          You can also contact us at the email address above and request to opt out, view, export or
          delete your data.
          <br className="br-2x" />
          If you request for your data to be deleted, your name and email address will be added to
          an exceptions list and all other data removed to the extent possible, except where we are
          legally obliged to retain the data or where we need your data to fulfil our service to
          you.
        </p>
        <h2 className="mb-6 text-xl font-bold mt-7">Data Protection Complaints</h2>
        <p>
          We always try to meet the highest standards of Data Privacy when collecting and using
          personal information. For this reason, we take any complaints we receive very seriously.
          We encourage people to bring it to our attention if they think that our collection or use
          of information is unfair, misleading or inappropriate.
        </p>
        <p>
          You have the right to complain about how we use your personal data. If you are unhappy or
          have concerns, please contact us first using the details in the &quot;Our Contact
          Details&quot; section above, so we can try to resolve the issue.
        </p>
        <p>
          You also have the right to make a complaint to the UK Information Commission (IC).
          However, the law now requires you to raise your complaint with us before complaining to
          the IC. If you do complain to us, we will acknowledge your complaint within 30 days,
          investigate it fairly and promptly, and write to you with our response.
        </p>
        <p>
          You have the right to complain to the UK IC at any time, using the contact information
          below. Please ensure that you state &lsquo;Data Protection Complaint&rsquo; in the subject
          field.
          <br className="br-2x" />
          <br className="br-2x" />
          The Information Commission&rsquo;s address:
          <br className="br-2x" />
          Information Commission
          <br />
          Wycliffe House
          <br />
          Water Lane
          <br />
          Wilmslow
          <br />
          Cheshire
          <br />
          SK9 5AF
          <br className="br-2x" />
          Helpline number: 0303 123 1113
          <br className="br-2x" />
          ICO website:{" "}
          <a
            href="https://www.ico.org.uk"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            https://www.ico.org.uk
          </a>
        </p>
        <p>
          Under the UK Data (Use and Access) Act 2025, the IC typically requires you to have
          completed our internal complaints process, before they will investigate.
        </p>
        <h2 className="mb-6 text-xl font-bold mt-7">Legal Compliance</h2>
        <p>
          We seek to uphold our legal obligations as covered by the Data Protection Act 2018, Data
          Use and Access Act 2025 and the General Data Protection Regulation 2016. Our Data
          Protection Authority is designated as the Information Commission (IC) formally the
          Information Commissioners Office (ICO).
          <br className="br-2x" />
          This Privacy Policy is reviewed on a regular basis and was last reviewed in May 2026.
        </p>
      </div>
    </>
  );
};

Privacy.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout theme="light">{page}</MainLayout>;
};

export default Privacy;
